import { useState, useEffect } from "react";
import "./FormHeader.css";
import AvatarMenu from "../avatar-menu/AvatarMenu";
import { useDispatch, useSelector } from "react-redux";
import { selectEditingForm, selectEditingQuestionId, selectUIStates, selectUser, setEditingForm, setEditingQuestionId, setUIStates } from "../../redux/features/appData";
import { handleCopyText, themeIcon } from "../shared/uiHelpers";
import { ICONS } from "../shared/media";
import { useNavigate } from "react-router-dom";
import { RenameFormModal } from "./RenameFormModal";
import { FormSettings } from "../form-settings/FormSettings";
import { CopyFormPayload, FormChanges, FormExtended, iconPath, ShadowCopyFormResponsesPayload, UpdateFormPublicLinkPayload, UpdateFormPublishedVersionIdPayload } from "effno-core";
import PreviewMenu from "./PreviewMenu";
import { apiCopyForm, apiCreateFormInWorkspace, apiCreateFormPublicLink, apiDeleteForm, apiFormHasNoEdits, apiGetForm, apiUpdateFormPublicLink, apiUpdateFormPublishedVersionId } from "../../services/FormService";
import { apiShadowCopyFormResponses } from "../../services/ResponseService";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { FirstPublishModal } from "./FirstPublishModal";
import { MediaManager } from "../media-manager/MediaManager";
import { Brush, Link, ClipboardList, ExternalLink, Settings, Share, SquarePlay, Wrench, Rocket, MessageSquareText } from "lucide-react";
import config from "environment";

const clientUrl = config.clientUrl;

enum FormHeaderColors {
    BUILD = "#92C5FD",
    DESIGN = "#FCA5A5",
    SETTINGS = "#87EEAB",
    PREVIEW = "#5EEAD3",
    RESPONSES = "#F8A8D4",
    SHARE = "#D7C5ED",
    COLLAB = "#FDE046",
    LINK = "#FDE046",
    OPEN = "#FDE046",
}

type FormHeaderProps = {
}

export const FormHeader = ({} : FormHeaderProps) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);
    const editingForm = useSelector(selectEditingForm);
    const editingQuestionId = useSelector(selectEditingQuestionId);

    const [showRename, setShowRename] = useState(false);
    const [showFirstPublish, setShowFirstPublish] = useState(false);
    const [firstPublishForm, setFirstPublishForm] = useState<FormExtended | null>(null);

    const handleClose = () => {
        dispatch(setEditingForm(null));
        dispatch(setEditingQuestionId(null))
        navigate("/home");
    }

    const handleEditName = () => {
        console.log("[FormHeader/handleEditName] edit name");
        setShowRename(true);
    }

    const handlePublish = async () => {
        const neverPublished = !editingForm?.publishedVersionId;

        // NR this is a temp test of shadow copy
        // 1. copy the form
        // 2. shadow copy responses of current form to new copy
        // 3. if there was an existing published version, mark the new form with existing public link
        //      if not, create a new one
        // 4. connect the editing one to the new published form
        // 5. if there was a previous published one, delete it

        if (!storeUser?.currentWorkspaceId) {
            console.log("[FormHeader/handlePublish] no workspace");
            return;
        }

        if (!editingForm)  {
            console.log("[FormHeader/handlePublish] no editingForm");
            return;
        }

        // save this as we are going to need it
        const originalFormChanges = JSON.parse(editingForm.formChanges || "[]") as FormChanges[];

        // if there is a published shadow, get it now as we'll need some of it later
        let currentPublishedForm : FormExtended | null = null;

        if (editingForm.publishedVersionId) {
            currentPublishedForm = await apiGetForm(editingForm.publishedVersionId);

            if (!currentPublishedForm) {
                toast("Failed to get current published form", toastFail);
                return;
            }
        }
        
        // make a copy of the current editing form
        const copyPayload : CopyFormPayload = {
            formId: editingForm.id,
            name: "Published version of " + editingForm.id + " | " + new Date().toLocaleString(),
            includeWorkflow: true,
            createMap: true,
        }

        // note this will overwrite the original formChanges in the editing form
        // which is why we saved it above
        let newPublishedForm = await apiCopyForm(copyPayload);

        if (!newPublishedForm) {
            toast("Failed to copy form for publishing", toastFail);
            return;
        }

        let updatedEditingForm = await apiGetForm(editingForm.id);

        if (!updatedEditingForm) {
            toast("Failed to get updated editing form for new map", toastFail);
            return;
        }

        const updatedEditingFormChanges = JSON.parse(updatedEditingForm.formChanges || "[]") as FormChanges[];

        // if there is a previously published version:
        // 1. copy all the responses from the previously publish form to the newly published form
        //    and get the question map between the two published forms
        // 2. update the map from the original form to the new published form
        // 3. delete anything in the new responses that was delete from the editing form
        console.log("[FormHeader/handlePublish] originalFormChanges: ", originalFormChanges);
        console.log("[FormHeader/handlePublish] updatedEditingFormChanges: ", updatedEditingFormChanges);

        if (currentPublishedForm) {
            // make the new map which connects the existing published form questions
            // to the new published form questions via the two maps
            const newFormChanges : FormChanges[] = [];
            
            if (originalFormChanges && originalFormChanges.length > 0) {
                for (let i = 0; i < originalFormChanges.length; i++) {
                    const originalMap = originalFormChanges[i];

                    const newIndex = updatedEditingFormChanges.findIndex(c => 
                        c.originalQuestionId === originalMap.originalQuestionId);

                    if (newIndex > -1) {
                        newFormChanges.push({
                            type: originalMap.type,
                            originalQuestionId: originalMap.newQuestionId,
                            newQuestionId: updatedEditingFormChanges[newIndex].newQuestionId
                        });
                    }
                }
            }

            // prep the changes
            const responsesPayload: ShadowCopyFormResponsesPayload = {
                originalFormId: currentPublishedForm.id,
                newFormId: newPublishedForm.id,
                changes: newFormChanges
            }

            const newFormResponses = await apiShadowCopyFormResponses(responsesPayload);
            console.log("[FormHeader/handlePublish] newFormResponses: ", newFormResponses);
        }

        // if published copy exists
        if (currentPublishedForm) {
            const publicLinkPayload : UpdateFormPublicLinkPayload = {
                formId: newPublishedForm.id,
                publicLink: currentPublishedForm.publicLink || "",  // this should never be blank
            }

            newPublishedForm = await apiUpdateFormPublicLink(publicLinkPayload);

            if (!newPublishedForm) {
                toast("Failed to update public link for published form", toastFail);
                return;
            }

            // delete the old published form
            await apiDeleteForm(currentPublishedForm.id);
        } else {
            newPublishedForm = await apiCreateFormPublicLink(newPublishedForm.id);

            if (!newPublishedForm) {
                toast("Failed to create public link for new published form", toastFail);
                return;
            }
        }

        // update the editing form to point to the new published form
        const publishedVersionIdPayload : UpdateFormPublishedVersionIdPayload = {
            formId: editingForm.id,
            publishedVersionId: newPublishedForm.id,
        }

        updatedEditingForm = await apiUpdateFormPublishedVersionId(publishedVersionIdPayload);

        if (!updatedEditingForm) {
            toast("Failed to update editing form with new published version", toastFail);
            return;
        }

        await apiFormHasNoEdits(updatedEditingForm.id);
        updatedEditingForm.hasEdits = false;
        
        dispatch(setEditingForm(updatedEditingForm));

        toast("Form published", toastSuccess);

        if (neverPublished) {
            setShowFirstPublish(true);
            setFirstPublishForm(updatedEditingForm);
        }

        return;
    }

    const handleViewResponses = async() => {
        if (!uiStates.viewingResponses) {
            dispatch(setUIStates({
                ...uiStates,
                viewingBuild: false,
                viewingDesign: false,
                viewingSettings: false,
                viewingPreview: false,
                viewingComments: false,
                viewingResponses: true,
                viewingShare: false,
            }));

            const publishedForm = await apiGetForm(editingForm?.publishedVersionId || "");

            if (publishedForm) {
                navigate(`/responses/${publishedForm.publicLink}`)
            }
        }
    }

    const handleCopyLink =  async () => {
        if (!editingForm?.publishedVersionId) {
            toast("Form must be published first", toastFail);
            return;
        }

        const publishedVersion = await apiGetForm(editingForm.publishedVersionId);

        if (publishedVersion) {
            await handleCopyText(clientUrl + "/form/" + publishedVersion.publicLink);
        }
    }

    const handleOpenLink = async () => {
        if (!editingForm?.publishedVersionId) {
            toast("Form must be published to share", toastFail);
            return;
        }

        const publishedVersion = await apiGetForm(editingForm.publishedVersionId);

        if (publishedVersion) {
            window.open(clientUrl + "/form/" + publishedVersion.publicLink, "_blank");
        }
    }

        
    const workspace = storeUser?.workspaces?.find(w => w.id === editingForm?.workspaceId);
    const hasQuestions = editingForm ? editingForm.questions?.length > 0 : false;
    const wasPublished = editingForm?.publishedVersionId ? true : false;
    
    return (
        <>
            <div 
                className="form-header-name"
            >
                <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
                    onClick={handleClose}
                    // class="lucide lucide-arrow-left"
                    >
                        <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
                </svg>
                
                <div className="form-header-name-divider">{" "}</div>
                <span 
                    className="form-header-form-name"
                    onClick={handleEditName}
                >
                    {editingForm?.name.substring(0, 20)}{(editingForm && editingForm.name.length > 20) ? "..." : ""}
                </span>
            </div>
            <div className="form-header-steps">
                <div 
                    className={uiStates.viewingBuild ?
                        "form-header-step-active" : "form-header-step"}
                    onClick={()=> {
                        dispatch(setUIStates({
                                ...uiStates,
                                viewingBuild: true,
                                viewingDesign: false,
                                viewingSettings: false,
                                viewingPreview: false,
                                viewingComments: false,
                                viewingResponses: false,
                                viewingShare: false,
                                commentMode: false,
                            }));
                        navigate("/editor")
                    }}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.BUILD;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingBuild ? FormHeaderColors.BUILD : "black";
                    // }}
                    data-testid="form-build"
                    // style={{backgroundColor: FormHeaderColors.BUILD}}
                >
                    <Wrench 
                        size={16}
                        color={FormHeaderColors.BUILD}
                    />
                    <span style={uiStates.viewingBuild ? 
                        {color: FormHeaderColors.BUILD, fontWeight: 600} : {}
                        }
                    >
                        build
                    </span>
                </div>
                <div 
                    className={uiStates.viewingDesign ?
                        "form-header-step-active" : "form-header-step"}
                    style={
                        {
                            ...(hasQuestions ? {} : {pointerEvents: "none", opacity: 0.5})
                        }
                    }
                    onClick={()=> {
                        dispatch(setUIStates({
                            ...uiStates,
                            viewingBuild: false,
                            viewingDesign: true,
                            viewingSettings: false,
                            viewingPreview: false,
                            viewingComments: false,
                            viewingResponses: false,
                            viewingShare: false,
                            openingDesign: true,
                            commentMode: false,
                        }));
                        navigate("/editor")
                    }}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.DESIGN;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingDesign ? FormHeaderColors.DESIGN : "black";
                    // }}
                    data-testid="form-design"
                    // style={{backgroundColor: FormHeaderColors.DESIGN}}
                >
                    <Brush  
                        size={16}
                        color={FormHeaderColors.DESIGN}
                    />
                    <span style={uiStates.viewingDesign ? 
                        {color: FormHeaderColors.DESIGN, fontWeight: 600} : {}
                        }
                    >
                        design
                    </span>
                </div>
                <div 
                    className={uiStates.viewingSettings ?
                        "form-header-step-active" : "form-header-step"}
                    style={
                        {
                            // backgroundColor: FormHeaderColors.RESPONSES,
                            ...(hasQuestions ? {} : {pointerEvents: "none", opacity: 0.5})
                        }
                    }
                    onClick={()=>{
                        dispatch(setUIStates({
                            ...uiStates,
                            viewingBuild: false,
                            viewingDesign: false,
                            viewingSettings: true,
                            viewingPreview: false,
                            viewingComments: false,
                            viewingResponses: false,
                            viewingShare: false,
                            formSettingsOpen: true,
                            commentMode: false,
                        }));
                        navigate("/form-settings/");
                    }}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.SETTINGS;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingSettings ? FormHeaderColors.SETTINGS : "black";
                    // }}
                    data-testid="form-settings"
                    // style={{backgroundColor: FormHeaderColors.SETTINGS}}
                >
                    <Settings 
                        size={16}
                        color={FormHeaderColors.SETTINGS}
                    />
                    <span 
                        style={
                            uiStates.viewingSettings ? 
                                {color: FormHeaderColors.SETTINGS, fontWeight: 600} : {}
                        }
                    >
                        settings
                    </span>
                </div>
                <div 
                    className="form-header-step"
                    style={
                        {
                            // backgroundColor: FormHeaderColors.PREVIEW, 
                            ...(hasQuestions ? {} : {pointerEvents: "none", opacity: 0.5})
                        }
                    }
                    onClick={()=> {
                        dispatch(setUIStates({
                            ...uiStates,
                            viewingBuild: false,
                            viewingDesign: false,
                            viewingSettings: false,
                            viewingPreview: true,
                            viewingComments: false,
                            viewingResponses: false,
                            viewingShare: false,
                            commentMode: false,
                        }));
                        // navigate("/preview/" + editingForm?.id + "?question=" + editingQuestionId)
                        navigate("/preview/" + editingForm?.id)

                    }}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.PREVIEW;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingPreview ? FormHeaderColors.PREVIEW : "black";
                    // }}
                    data-testid="form-preview"
                >
                    <SquarePlay 
                        size={16}
                        color={FormHeaderColors.PREVIEW}
                    />
                    <span style={uiStates.viewingPreview ? 
                        {color: FormHeaderColors.PREVIEW, fontWeight: 600} : {}
                        }
                    >
                        preview
                    </span>
                    {/* <div className="form-header-submenu">
                        <PreviewMenu />
                    </div> */}
                </div>
                <div 
                    className="form-header-step"
                    style={
                        {
                            // backgroundColor: FormHeaderColors.PREVIEW, 
                            ...(hasQuestions ? {} : {pointerEvents: "none", opacity: 0.5})
                        }
                    }
                    onClick={()=> {
                        dispatch(setUIStates({
                            ...uiStates,
                            viewingBuild: false,
                            viewingDesign: false,
                            viewingSettings: false,
                            viewingPreview: false,
                            viewingComments: true,
                            viewingResponses: false,
                            viewingShare: false,
                            commentMode: true,
                            mobileLayout: false,
                        }));
                        navigate("/editor")
                    }}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.COLLAB;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingComments ? FormHeaderColors.COLLAB : "black";
                    // }}
                    data-testid="form-collab"
                >
                    <MessageSquareText 
                        size={16}
                        color={FormHeaderColors.COLLAB}
                    />
                    <span style={uiStates.viewingComments ? 
                        {color: FormHeaderColors.COLLAB, fontWeight: 600} : {}
                        }
                    >
                        comments
                    </span>
                    {/* <div className="form-header-submenu">
                        <PreviewMenu />
                    </div> */}
                </div>
                <div 
                    className={uiStates.viewingResponses ? 
                        "form-header-step-active" : "form-header-step"}
                    style={
                        {
                            // backgroundColor: FormHeaderColors.RESPONSES,
                        ...((hasQuestions && wasPublished) ? 
                            {} : {pointerEvents: "none", opacity: 0.5})
                        }
                    }
                    onClick={handleViewResponses}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.RESPONSES;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingResponses ? FormHeaderColors.RESPONSES : "black";
                    // }}
                    data-testid="form-responses"
                >
                    <ClipboardList 
                         size={16}
                         color={FormHeaderColors.RESPONSES}
                    />
                    <span style={uiStates.viewingResponses ? 
                        {color: FormHeaderColors.RESPONSES, fontWeight: 600} : {}
                        }
                    >
                        responses
                    </span>

                </div>
                <div 
                    className={uiStates.viewingShare ?
                        "form-header-step-active" : "form-header-step"}
                    style={
                        {
                            // backgroundColor: FormHeaderColors.SHARE,
                            ...((hasQuestions && editingForm?.publishedVersionId) ? 
                                {} : {pointerEvents: "none", opacity: 0.5})
                        }
                    }
                    onClick={()=> {
                        if (!uiStates.viewingShare) {
                            dispatch(setUIStates({
                                ...uiStates,
                                viewingBuild: false,
                                viewingDesign: false,
                                viewingSettings: false,
                                viewingPreview: false,
                                viewingComments: false,
                                viewingResponses: false,
                                viewingShare: true,
                                commentMode: false,
                            }));
                            navigate(`/share/${editingForm?.publishedVersionId}`)
                        }
                    }}
                    // onMouseEnter={(e: any) => {
                    //     e.target.style.color = FormHeaderColors.SHARE;
                    // }}
                    // onMouseLeave={(e: any) => {
                    //     e.target.style.color = uiStates.viewingShare ? FormHeaderColors.SHARE : "black";
                    // }}
                    data-testid="form-share"
                >
                    <Share 
                         size={16}
                         color={FormHeaderColors.SHARE}
                    />
                    <span style={uiStates.viewingShare ? 
                        {color: FormHeaderColors.SHARE, fontWeight: 600} : {}
                        }
                    >
                        share
                    </span>
                </div>
            </div>
            <div className="form-header-actions">
                {/* <div 
                    className="form-header-icon"
                    onClick={()=>dispatch(setUIStates({...uiStates, formSettingsOpen: true}))}
                >
                    <img src={themeIcon(storeUser, ICONS.SETTINGS)} />
                </div> */}
               
                <div 
                    className="form-header-icon"
                    onClick={handleCopyLink}
                >
                    <Link size={16}/>
                </div>
                 <div 
                    className="form-header-icon"
                    onClick={handleOpenLink}
                >
                    <ExternalLink size={16}/>
                </div>

                <div className="form-header-publish">
                    <button
                        style={(hasQuestions && editingForm?.hasEdits) ? {} : {pointerEvents: "none", opacity: 0.5}}
                        // onClick={()=> navigate(`/form/${editingForm?.publicLink}`)}
                        onClick={handlePublish}
                        data-testid="form-publish"
                    > 
                        <div className="form-header-publish-insides">
                            <Rocket size={16}/>
                            Publish
                        </div>
                        
                    </button>
                </div>
                {/* <div className="form-header-menu">
                    <AvatarMenu image={storeUser?.avatarURL || ""} />
                </div> */}
            </div>

            {showRename &&
                <RenameFormModal 
                    isOpen={showRename}
                    onClose={()=>setShowRename(false)}
                />
            }
            
            {showFirstPublish && firstPublishForm &&
                <FirstPublishModal 
                    isOpen={showFirstPublish}
                    onClose={()=>setShowFirstPublish(false)}
                    form={firstPublishForm}
                />
            }
            
            {/* {uiStates.formSettingsOpen &&
                <FormSettings 
                    isOpen={uiStates.formSettingsOpen}
                    onClose={
                        () => dispatch(setUIStates({...uiStates, formSettingsOpen: false}))
                    }
                />
            } */}

            {uiStates.mediaManagerOpen &&
                <MediaManager 
                    isOpen={uiStates.mediaManagerOpen}
                    noSelect={true}
                    onClose={() => dispatch(setUIStates({...uiStates, mediaManagerOpen: false}))}
                    onSelectedAsset={()=>{}}
                />
            }
        </>
        
    )
}
